(() => {
  'use strict';

  const VARIANT_ID = '47798284517652';
  const DEFAULT_QUANTITY = 1;
  const UNIT_PRICE = 40;
  const ALLOWED_HOSTS = new Set(['shop.mclaughlingolf.com', '40977b.myshopify.com']);
  const ATTRIBUTION_KEYS = [
    'utm_id',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_source_platform',
    'gclid',
    'fbclid',
  ];

  function parseQuantity(value, maximum) {
    const normalized = String(value);
    if (!/^[1-9]\d*$/.test(normalized)) return null;
    const quantity = Number(normalized);
    return Number.isSafeInteger(quantity) && quantity <= maximum ? quantity : null;
  }

  function normalizeCheckoutUrl(rawUrl, requestedQuantity, maximum) {
    try {
      const quantity = parseQuantity(requestedQuantity, maximum);
      if (!quantity) return null;

      const url = new URL(rawUrl, window.location.origin);
      const validPath = new RegExp(`^/cart/${VARIANT_ID}:[1-9]\\d*$`).test(url.pathname);
      const validQuery = url.searchParams.get('skip_shop_pay') === 'true' &&
        Array.from(url.searchParams.keys()).every((key) => key === 'skip_shop_pay' || ATTRIBUTION_KEYS.includes(key));

      if (
        url.protocol !== 'https:' ||
        !ALLOWED_HOSTS.has(url.hostname.toLowerCase()) ||
        url.username ||
        url.password ||
        url.port ||
        url.hash ||
        !validPath ||
        !validQuery
      ) {
        return null;
      }

      url.pathname = `/cart/${VARIANT_ID}:${quantity}`;
      url.search = '';
      url.searchParams.set('skip_shop_pay', 'true');
      const landingParams = new URLSearchParams(window.location.search);
      for (const key of ATTRIBUTION_KEYS) {
        const value = landingParams.get(key)?.trim();
        if (value) url.searchParams.set(key, value.slice(0, 200));
      }
      return url.toString();
    } catch (_error) {
      return null;
    }
  }

  function initializeCampaign(shell) {
    if (!(shell instanceof HTMLElement) || shell.dataset.enduraliteInitialized === 'true') return;
    shell.dataset.enduraliteInitialized = 'true';

    const checkoutLinks = Array.from(shell.querySelectorAll('[data-campaign-checkout]'));
    const checkoutBaseUrls = new Map(checkoutLinks.map((link) => [link, link.getAttribute('href')]));
    const maximumQuantity = Number(shell.dataset.campaignInventoryMax);
    const inventoryIsBounded = Number.isSafeInteger(maximumQuantity) && maximumQuantity >= DEFAULT_QUANTITY;
    const quantityInput = shell.querySelector('[data-quantity-input]');
    const decreaseButton = shell.querySelector('[data-quantity-decrease]');
    const increaseButton = shell.querySelector('[data-quantity-increase]');
    const quantityStatus = shell.querySelector('[data-quantity-status]');
    let lastValidQuantity = DEFAULT_QUANTITY;

    const disableCheckout = () => {
      for (const link of checkoutLinks) {
        link.setAttribute('href', '#purchase');
        link.setAttribute('aria-disabled', 'true');
        link.dataset.campaignCheckoutDisabled = 'true';
      }
    };

    const setStatus = (message) => {
      if (quantityStatus) quantityStatus.textContent = message;
    };

    const updateQuantity = (requestedQuantity, announce = true) => {
      const quantity = inventoryIsBounded ? parseQuantity(requestedQuantity, maximumQuantity) : null;
      if (!quantity) {
        return false;
      }

      const nextUrls = checkoutLinks.map((link) => normalizeCheckoutUrl(checkoutBaseUrls.get(link), quantity, maximumQuantity));
      if (nextUrls.some((url) => !url)) {
        disableCheckout();
        return false;
      }

      for (const [index, link] of checkoutLinks.entries()) {
        link.setAttribute('href', nextUrls[index]);
        link.setAttribute('data-quantity', String(quantity));
        link.removeAttribute('aria-disabled');
        delete link.dataset.campaignCheckoutDisabled;
      }

      lastValidQuantity = quantity;
      const total = `$${quantity * UNIT_PRICE}`;
      const quantityLabel = `${quantity} ${quantity === 1 ? 'hat' : 'hats'}`;
      shell.querySelectorAll('[data-campaign-price-label], [data-campaign-total]').forEach((element) => {
        element.textContent = total;
      });
      shell.querySelectorAll('[data-campaign-quantity-label]').forEach((element) => {
        element.textContent = quantityLabel;
      });
      shell.querySelectorAll('[data-campaign-quantity-value]').forEach((element) => {
        element.textContent = String(quantity);
      });
      if (quantityInput) {
        quantityInput.value = String(quantity);
        quantityInput.setAttribute('aria-valuenow', String(quantity));
        quantityInput.removeAttribute('aria-invalid');
      }
      if (decreaseButton) decreaseButton.disabled = quantity <= 1;
      if (increaseButton) increaseButton.disabled = quantity >= maximumQuantity;
      if (announce) setStatus(`${quantityLabel} selected. Total ${total}.`);
      return true;
    };

    const rejectQuantity = () => {
      if (quantityInput) {
        quantityInput.value = String(lastValidQuantity);
        quantityInput.setAttribute('aria-valuenow', String(lastValidQuantity));
        quantityInput.setAttribute('aria-invalid', 'true');
      }
      const total = `$${lastValidQuantity * UNIT_PRICE}`;
      setStatus(`Enter a whole number from 1 to ${maximumQuantity}. Quantity remains ${lastValidQuantity}; total ${total}.`);
    };

    if (!inventoryIsBounded || !updateQuantity(DEFAULT_QUANTITY, false)) {
      disableCheckout();
      if (quantityInput) {
        quantityInput.disabled = true;
        quantityInput.setAttribute('aria-invalid', 'true');
      }
      if (decreaseButton) decreaseButton.disabled = true;
      if (increaseButton) increaseButton.disabled = true;
      setStatus('Purchasing is unavailable until current sellable inventory can be confirmed.');
    } else {
      setStatus('1 hat selected. Total $40.');
      quantityInput?.addEventListener('keydown', (event) => {
        if (['e', 'E', '+', '-', '.'].includes(event.key)) event.preventDefault();
      });
      quantityInput?.addEventListener('input', () => {
        if (quantityInput.value === '') {
          quantityInput.setAttribute('aria-invalid', 'true');
          return;
        }
        if (!updateQuantity(quantityInput.value)) rejectQuantity();
      });
      quantityInput?.addEventListener('change', () => {
        if (!updateQuantity(quantityInput.value)) rejectQuantity();
      });
      quantityInput?.addEventListener('blur', () => {
        if (!updateQuantity(quantityInput.value)) rejectQuantity();
      });
      decreaseButton?.addEventListener('click', () => updateQuantity(lastValidQuantity - 1));
      increaseButton?.addEventListener('click', () => updateQuantity(lastValidQuantity + 1));
    }

    const gallery = shell.querySelector('[data-campaign-gallery]');
    if (gallery) {
      const stage = gallery.querySelector('[data-gallery-stage]');
      const stageImage = stage?.querySelector('img');
      const group = gallery.querySelector('[data-gallery-group]');
      const title = gallery.querySelector('[data-gallery-title]');
      const detail = gallery.querySelector('[data-gallery-detail]');
      const map = gallery.querySelector('[data-gallery-map]');
      const buttons = Array.from(gallery.querySelectorAll('[data-gallery-button]'));

      for (const [index, button] of buttons.entries()) {
        button.addEventListener('click', () => {
          if (!stage || !stageImage) return;
          for (const candidate of buttons) {
            candidate.classList.toggle('is-active', candidate === button);
            candidate.setAttribute('aria-pressed', candidate === button ? 'true' : 'false');
          }
          stage.classList.toggle('is-tight', button.dataset.tight === 'true');
          stage.classList.toggle('is-editorial', button.dataset.editorial === 'true');
          stageImage.src = button.dataset.src || '';
          stageImage.srcset = button.dataset.srcset || '';
          stageImage.alt = button.dataset.alt || '';
          if (group) group.textContent = button.dataset.group || '';
          if (title) title.textContent = button.dataset.title || '';
          if (detail) detail.textContent = button.dataset.detail || '';
          if (map) map.textContent = `DETAIL VIEW / ${String(index + 1).padStart(2, '0')}`;
        });
      }
    }

    const purchaseControls = shell.querySelector('[data-purchase-controls]');
    const sticky = shell.querySelector('[data-sticky-buy]');
    if (purchaseControls && sticky && 'IntersectionObserver' in window) {
      let controlsVisible = false;
      const updateSticky = () => {
        sticky.hidden = !(window.scrollY > window.innerHeight * 0.65 && !controlsVisible);
      };
      const observer = new IntersectionObserver(([entry]) => {
        controlsVisible = entry.isIntersecting;
        updateSticky();
      }, { threshold: 0.15 });
      observer.observe(purchaseControls);
      window.addEventListener('scroll', updateSticky, { passive: true });
      window.addEventListener('resize', updateSticky);
      updateSticky();
    }
  }

  function initializeAll(root = document) {
    if (root.matches?.('[data-enduralite-campaign]')) initializeCampaign(root);
    root.querySelectorAll?.('[data-enduralite-campaign]').forEach(initializeCampaign);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
